import { typesense } from '../typesenseClient';
import type { SearchError, UnionMultiSearchResult } from './search.types';
import {
  TypesenseCollection,
  type TypesenseCollectionName,
  type TypesenseKeyName,
} from '../types';
import { TypesenseAPIKeys } from '@/api/REST/typeSenseAPIKeys';

type CollectionMap = Record<
  string,
  {
    name: TypesenseCollectionName;
    keyName: TypesenseKeyName;
    queryableFields?: string;
    filter?: string;
    infix?: string;
  }
>;

export const collectionMapOnNameWithoutActions: CollectionMap = {
  Project: {
    ...TypesenseCollection.PROJECTS,
    queryableFields: 'Name,Number',
    filter: 'Status:[0..4] && IsProjectTemplate:!=true',
    // Set Number to off until we can fix infix issues
    infix: 'off',
  },
  Customer: {
    ...TypesenseCollection.CUSTOMERS,
    queryableFields: 'Name,Number',
    filter: 'Active:true && IsCustomer:true',
  },
  Employee: {
    ...TypesenseCollection.EMPLOYEES,
    queryableFields: 'Name,Initials',
    filter: 'Employed:true',
  },
  Invoice: {
    ...TypesenseCollection.INVOICES,
    queryableFields: 'InvoiceNo',
  },
  Contact: {
    ...TypesenseCollection.CONTACTS,
    queryableFields: 'Name',
    filter: 'IsActive:true',
  },
  Dashboard: {
    ...TypesenseCollection.DASHBOARDS,
    queryableFields: 'Name,OwnerName',
  },
};

export const collectionMapWithoutActions: CollectionMap = {
  Project: {
    ...TypesenseCollection.PROJECTS,
    queryableFields:
      'Name,Number,PoNumber,ProjectManagerName,PartnerName,AccountManagerName,CustomerName,CustomerNumber',
    filter: 'Status:[0..4] && IsProjectTemplate:!=true',
  },
  Customer: {
    ...TypesenseCollection.CUSTOMERS,
    queryableFields:
      'Name,Number,Nickname,VatNumber,OrganizationNumber,AccountManagerName,SecondaryAccountManagerName,Phone',
    filter: 'Active:true && IsCustomer:true',
  },
  Employee: {
    ...TypesenseCollection.EMPLOYEES,
    queryableFields:
      'FirstName,LastName,InternalId,Email,Position,RoleName,Mobile,PrivatePhone,Competencies',
    filter: 'Employed:true',
  },
  Invoice: {
    ...TypesenseCollection.INVOICES,
    queryableFields:
      'Name,PurchaseNo,Header,CustomerName,CustomerNumber,InvoiceNo,ProjectName,ProjectNo,CustomerVATNo',
  },
  Contact: {
    ...TypesenseCollection.CONTACTS,
    queryableFields:
      'FirstName,LastName,Email,CustomerName,JobTitle,Owner,Phone,Mobile,HomePhone',
    filter: 'IsActive:true',
  },
  Dashboard: {
    ...TypesenseCollection.DASHBOARDS,
    queryableFields: 'Name,OwnerName',
  },
};

export const getCollectionWithoutActionsSearchRequests = (
  apiKeys: TypesenseAPIKeys,
  collectionMapWithoutActions: CollectionMap,
) => {
  return {
    searches: Object.keys(collectionMapWithoutActions)
      .filter((item) => !!apiKeys[collectionMapWithoutActions[item].keyName]) // only keep entries with api keys present
      .map((item) => ({
        collection: collectionMapWithoutActions[item].name,
        'x-typesense-api-key':
          apiKeys[collectionMapWithoutActions[item].keyName].key,
        query_by: collectionMapWithoutActions[item].queryableFields
          ? collectionMapWithoutActions[item].queryableFields
          : '*',
        exclude_fields: 'embedding,TenantId',
        ...('filter' in collectionMapWithoutActions[item] &&
          collectionMapWithoutActions[item].filter && {
            filter_by: collectionMapWithoutActions[item].filter,
          }),
        ...('infix' in collectionMapWithoutActions[item] &&
          collectionMapWithoutActions[item].infix && {
            infix: collectionMapWithoutActions[item].infix,
          }),
      })),
  };
};

export const getSearch = (
  searchValue: string,
  apiKeys: TypesenseAPIKeys,
  collectionToSearch: (
    apiKeys: TypesenseAPIKeys,
    collectionMap: CollectionMap,
  ) => { searches: unknown[] } | null,
  collectionMap: CollectionMap,
  perPage: number = 20,
  sortBy?: string,
): Promise<UnionMultiSearchResult | SearchError> => {
  const searchRequests =
    apiKeys && collectionMap
      ? collectionToSearch(apiKeys, collectionMap)
      : null;

  const commonSearchParams = {
    q: searchValue,
    num_typos: 2,
    min_len_1typo: 3,
    per_page: perPage,
    ...(sortBy ? { sort_by: sortBy } : {}), // e.g.,
  };

  if (!searchRequests || !searchRequests.searches?.length) {
    // If no valid searches, return an empty result
    return Promise.resolve({
      found: 0,
      hits: [],
      out_of: 0,
      page: 0,
      search_cutoff: false,
      search_time_ms: 0,
      union_request_params: [],
    } as UnionMultiSearchResult);
  }

  // Cast to align with our MultiSearchResult type
  return typesense.multiSearch.perform(
    {
      // @ts-expect-error: union is still new in typesense client
      union: true,
      ...(searchRequests as unknown as Parameters<
        typeof typesense.multiSearch.perform
      >[0]),
    },
    commonSearchParams,
  ) as unknown as Promise<UnionMultiSearchResult | SearchError>;
};
